/**
 * Custom dropdown for a reader slot — no native select. 44px rows; options the
 * policy lists but has not enabled yet show greyed with "Coming next".
 */
import React, { useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';

export interface PickOption { id: string; label: string; sub?: string; disabled?: boolean; tag?: string; group?: string }

export const ReaderPicker: React.FC<{
  ariaLabel: string; value: string | null; placeholder: string; options: PickOption[];
  onChange: (id: string | null) => void; disabled?: boolean; noneLabel?: string;
}> = ({ ariaLabel, value, placeholder, options, onChange, disabled, noneLabel }) => {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.id === value);
  const pick = (id: string | null) => { onChange(id); setOpen(false); };
  return (
    <div className="relative">
      <button type="button" aria-label={ariaLabel} aria-haspopup="listbox" aria-expanded={open} disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        className="flex min-h-[44px] w-full items-center justify-between gap-2 rounded-lg border border-stone-300 bg-white px-3 text-left text-sm text-stone-800 hover:border-stone-400 disabled:opacity-50">
        <span className="min-w-0 truncate">{current ? current.label : (value === null && noneLabel) ? noneLabel : placeholder}</span>
        <ChevronDown size={16} className="shrink-0 text-stone-500" />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul role="listbox" aria-label={ariaLabel} className="absolute z-50 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-stone-200 bg-white py-1 shadow-lg">
            {noneLabel && (
              <li role="option" aria-selected={value === null}>
                <button type="button" onClick={() => pick(null)} className="flex min-h-[44px] w-full items-center justify-between px-3 text-left text-sm text-stone-700 hover:bg-stone-50">
                  {noneLabel}{value === null && <Check size={14} className="text-ruby" />}
                </button>
              </li>
            )}
            {options.map((o, i) => (
              <React.Fragment key={o.id}>
              {o.group && o.group !== options[i - 1]?.group && (
                <li role="presentation" className="mt-1 border-t border-stone-100 px-3 pb-1 pt-2 text-[10.5px] font-semibold uppercase tracking-wide text-stone-500">{o.group}</li>
              )}
              <li role="option" aria-selected={o.id === value} aria-disabled={o.disabled || undefined}>
                <button type="button" disabled={o.disabled} onClick={() => pick(o.id)}
                  className="flex min-h-[44px] w-full items-center justify-between gap-2 px-3 py-1.5 text-left hover:bg-stone-50 disabled:cursor-not-allowed disabled:opacity-50">
                  <span className="min-w-0">
                    <span className="block truncate text-sm text-stone-800">{o.label}</span>
                    {o.sub && <span className="block truncate text-[11.5px] text-stone-500">{o.sub}</span>}
                  </span>
                  {o.tag && <span className="shrink-0 rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-stone-500">{o.tag}</span>}
                  {o.id === value && <Check size={14} className="shrink-0 text-ruby" />}
                </button>
              </li>
              </React.Fragment>
            ))}
            {!options.length && !noneLabel && <li className="px-3 py-2 text-sm text-stone-500">Nothing to pick at this location</li>}
          </ul>
        </>
      )}
    </div>
  );
};

/** Segmented control — custom, 44px, ruby on light. */
export const Segmented: React.FC<{
  ariaLabel: string; value: string | null; options: Array<{ id: string; label: string; disabled?: boolean; title?: string }>;
  onChange: (id: string) => void; disabled?: boolean;
}> = ({ ariaLabel, value, options, onChange, disabled }) => (
  <div role="radiogroup" aria-label={ariaLabel} className="inline-flex flex-wrap rounded-lg border border-stone-300 bg-white p-0.5">
    {options.map((o) => {
      const on = o.id === value;
      return (
        <button key={o.id} type="button" role="radio" aria-checked={on} title={o.title}
          disabled={disabled || o.disabled} onClick={() => onChange(o.id)}
          className={`min-h-[40px] rounded-md px-3 text-[13px] font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${on ? 'bg-ruby text-white' : 'text-stone-700 hover:bg-stone-100'}`}>
          {o.label}
        </button>
      );
    })}
  </div>
);
